import { Table } from '@rabbit/elements/shared-components';
import { MRT_ColumnDef, MRT_TableOptions } from 'material-react-table';
import { ReactElement } from 'react';
import { formatDate, isValidDate, titleCase } from './helpers';

export type TableFields<T> = ({
  header: string;
  type?: 'Date' | 'Status';
  value: (arg0: T) => any;
  primary?: boolean;
  Header?: ReactElement;
} & MRT_ColumnDef<any>)[];

interface TableProps<T> extends Partial<MRT_TableOptions<any>> {
  className?: string;
  onClickRow?: (data: T) => any;
  advancedTable?: boolean;
}

export function CreateTable<T>(
  sourceData: T[],
  tableFields: TableFields<T>
): (props: TableProps<T>) => ReactElement<any, any> {
  const name = tableFields
    .map((field) => (field.header || ' ')[0])
    .join('')
    .replace(' ', '');

  const columns: MRT_ColumnDef<any>[] = tableFields.map((field) => {
    const id = field.header.replace(/ /, '_').toLowerCase();
    field.id = id.trim() !== '' ? id : field.accessorKey || 'id';
    const fieldFormat = {
      ...field,
      accessorKey: field.id,
      size: field.size ? field.size : field.primary ? 180 : 20,
    };
    if (field.type === 'Date') {
      fieldFormat.Cell = ({ cell }: any) => {
        if (cell.getValue()) {
          if (isValidDate(new Date(cell.getValue()))) {
            return formatDate(new Date(cell.getValue()));
          }
        }
        return '-';
      };
    }
    if (field.type === 'Status') {
      // Keep underlying value as a primitive for stable sorting/filtering/export,
      // and only render the pill in the cell.
      fieldFormat.Cell = ({ cell }: any) => parseStatus(cell.getValue());
    }
    return fieldFormat;
  });

  const record: any[] = [];
  sourceData.map((data) => {
    const row: any = {};
    tableFields.forEach((field) => {
      let value: any = field.value(data);
      // Store primitive status value; pill rendering is handled in Cell above.
      row[field.id as string] = value;
    });
    record.push(row);
  });

  return (props: TableProps<T>) => (
    <div className="relative z-0 w-full">
      <Table
        {...props}
        name={name}
        columns={columns}
        data={record}
        onSelectRow={(index: number) =>
          props.onClickRow && props.onClickRow(sourceData[index])
        }
      />
    </div>
  );
}

const parseStatus = (status: unknown) => {
  const statusStr =
    status === null || status === undefined || status === ''
      ? ''
      : String(status);
  const red = [
    'deleted',
    'inactive',
    'no',
    'voided',
    'expired',
    'outdated',
    'suspended',
    'rejected',
  ];
  const orange = ['pending', 'ongoing', 'in review', 'unsubmitted'];
  const green = ['active', 'yes', 'published', 'approved', 'accepted'];
  const style =
    red.indexOf(statusStr.toLowerCase()) > -1
      ? 'bg-red-100 text-red-800'
      : orange.indexOf(statusStr.toLowerCase()) > -1
      ? 'bg-orange-100 text-orange-800'
      : green.indexOf(statusStr.toLowerCase()) > -1
      ? 'bg-green-100 text-green-800'
      : 'bg-gray-300 text-gray-900 font-nunito';
  return (
    <div className={`inline-flex rounded-md px-3 py-[2px] ${style}`}>
      {statusStr ? titleCase(statusStr) : '-'}
    </div>
  );
};
